"use client";

import { useState, useEffect, useRef } from "react";
import {
  getVendorById,
  addVendorDocument,
  removeVendorDocument,
  updateVendorStatus,
  updateVendor,
  addVendorPerformance,
  addVendorContract,
} from "../../api/vendorApi";
import UploadDocumentModal from "../UploadDocumentModal";
import { toast } from "react-toastify";
import { FiX, FiUser, FiFileText, FiDollarSign, FiCalendar, FiFlag, FiSave, FiFolder, FiEdit, FiDownload, FiTrash2, FiPlus, FiStar, FiAward, FiCheck, FiLoader } from "react-icons/fi";

const VendorProfileModal = ({ vendorId, onClose, onVendorUpdated }) => {
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  const [newPerformance, setNewPerformance] = useState({
    date: "",
    score: "",
    comments: "",
  });
  const [newContract, setNewContract] = useState({
    title: "",
    contractNumber: "",
    startDate: "",
    endDate: "",
    value: "",
    type: "",
    description: "",
    status: "draft",
    renewalTerms: "",
    paymentTerms: "", 
    deliverables: "",
    contractDocument: null,
  });

  const [showAddPerformanceForm, setShowAddPerformanceForm] = useState(false);
  const [showAddContractForm, setShowAddContractForm] = useState(false);
  const [contractAddedSuccess, setContractAddedSuccess] = useState(false);
  const [isDocUploadModalOpen, setIsDocUploadModalOpen] = useState(false);
  const [isContractDocModalOpen, setIsContractDocModalOpen] = useState(false);
  
  // Ref for scrolling to contract form
  const contractFormRef = useRef(null);
  const modalRef = useRef(null);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the main modal
      if (modalRef.current && modalRef.current.contains(event.target)) {
        return;
      }
      
      // Specifically don't close if any upload modal is open
      if (isDocUploadModalOpen || isContractDocModalOpen) {
        return;
      }
      
      onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, isDocUploadModalOpen, isContractDocModalOpen]);

  // Reset contract form when it's hidden
  useEffect(() => {
    if (!showAddContractForm) {
      setNewContract({
        title: "",
        contractNumber: "",
        startDate: "",
        endDate: "",
        value: "",
        type: "",
        description: "",
        status: "draft",
        renewalTerms: "",
        paymentTerms: "",
        deliverables: "",
        contractDocument: null,
      });
    }
  }, [showAddContractForm]);

  const fetchVendor = async () => {
    setLoading(true);
    try {
      const response = await getVendorById(vendorId);
      setVendor(response.data.data);
      setEditFormData(response.data.data);
      return response.data.data;
    } catch (error) {
      toast.error("Failed to fetch vendor details. Please try again later.");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendor();
  }, [vendorId]);

  // Scroll to contract form when it's shown
  useEffect(() => {
    if (showAddContractForm && contractFormRef.current) {
      contractFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showAddContractForm]);

  const handleFormChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleAddressChange = (e) => {
    setEditFormData({
      ...editFormData,
      address: {
        ...editFormData.address,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleBankDetailsChange = (e) => {
    setEditFormData({
      ...editFormData,
      bankDetails: {
        ...editFormData.bankDetails,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleDocumentUploadSuccess = async (docs) => {
    setSubmitting(true);
    try {
      // The modal already uploaded to S3 and saved metadata in generic Document table.
      // Now we also add it to the Vendor's own documents array for direct access.
      for (const doc of docs) {
        const documentData = {
          type: "other",
          fileUrl: doc.fileUrl,
          s3Key: doc.key || "", // Assuming key is passed back or we can derive it
          fileName: doc.originalName || doc.fileName,
          fileSize: doc.fileSize || 0,
          mimeType: doc.fileType || doc.mimeType || "",
        };
        await addVendorDocument(vendorId, documentData);
      }
      toast.success("Documents added to vendor successfully! 📄");
      await fetchVendor();
    } catch (error) {
      console.error("Error linking documents:", error);
      toast.error("Failed to link documents to vendor profile.");
    } finally {
      setSubmitting(false);
    }
  };
  

  const handleDocumentRemove = async (docId) => {
    if (window.confirm("Are you sure you want to remove this document?")) {
      setSubmitting(true);
      try {
        await removeVendorDocument(vendorId, docId);
        toast.success("Document removed successfully! 🗑️");
        await fetchVendor();
      } catch (error) {
        const message = error.response?.data?.message || "Failed to remove document.";
        toast.error(`Removal failed: ${message}`);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setSubmitting(true);
    try {
      await updateVendorStatus(vendorId, newStatus);
      toast.success(`Vendor status updated to ${newStatus}. ✅`);
      const updatedVendor = await fetchVendor();
      if (typeof onVendorUpdated === 'function') onVendorUpdated(updatedVendor);
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update status.";
      toast.error(`Status update failed: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await updateVendor(vendorId, editFormData);
      toast.success("Vendor details updated successfully! 💾");
      setIsEditing(false);
      const updatedVendor = await fetchVendor();
      if (typeof onVendorUpdated === 'function') onVendorUpdated(updatedVendor);
    } catch (error) {
      const message = error.response?.data?.message || "Failed to save changes.";
      toast.error(`Save failed: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePerformanceChange = (e) => {
    setNewPerformance({ ...newPerformance, [e.target.name]: e.target.value });
  };

  const handleAddPerformance = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const performanceData = {
        date: newPerformance.date,
        rating: Math.min(5, Math.max(1, parseInt(newPerformance.score) || 1)),
        notes: newPerformance.comments,
      };
      await addVendorPerformance(vendorId, performanceData);
      toast.success("Performance record added successfully! 📈");
      await fetchVendor();
      setNewPerformance({ date: "", score: "", comments: "" });
      setShowAddPerformanceForm(false);
    } catch (error) {
      console.error("Performance error:", error);
      const message = error.response?.data?.message || "Failed to add performance record.";
      toast.error(`Performance record failed: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContractChange = (e) => {
    const { name, value } = e.target;
    setNewContract({
      ...newContract,
      [name]: value,
    });
  };

  const handleContractDocSuccess = (docs) => {
    if (docs && docs.length > 0) {
      const doc = docs[0];
      setNewContract(prev => ({
        ...prev,
        contractDocument: doc,
        // Also map to fields the backend might expect directly
        fileUrl: doc.fileUrl,
        fileName: doc.originalName,
        s3Key: doc.key,
        mimeType: doc.fileType || doc.mimeType,
        fileSize: doc.fileSize
      }));
    }
  };

  const handleAddContract = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (
        !newContract.title?.trim() ||
        !newContract.contractNumber?.trim() ||
        !newContract.startDate ||
        !newContract.endDate
      ) {
        toast.error("Please fill in all required fields (*)");
        setSubmitting(false);
        return;
      }
      let contractData = { ...newContract };
      
      // Document is already uploaded via modal and metadata is in newContract
      if (newContract.contractDocument) {
        const doc = newContract.contractDocument;
        contractData = {
          ...contractData,
          fileUrl: doc.fileUrl,
          s3Key: doc.key || doc.s3Key,
          fileName: doc.originalName || doc.fileName,
          fileSize: doc.fileSize,
          mimeType: doc.fileType || doc.mimeType,
        };
      }
      delete contractData.contractDocument;

      const { data: response } = await addVendorContract(vendorId, contractData);
      
      if (response.success) {
        // Show success message
        toast.success(`Contract "${newContract.title}" added successfully! 📋`);
        
        // Update vendor state with new contract
        setVendor(prevVendor => ({
          ...prevVendor,
          contracts: [...(prevVendor.contracts || []), response.data]
        }));
        
        // Reset form state
        setNewContract({
          title: "",
          contractNumber: "",
          startDate: "",
          endDate: "",
          value: "",
          type: "",
          description: "",
          status: "draft",
          renewalTerms: "",
          paymentTerms: "",
          deliverables: "",
          contractDocument: null,
        });
        
        // Show success state temporarily
        setContractAddedSuccess(true);
        setTimeout(() => setContractAddedSuccess(false), 3000);
        
        // Hide the form
        setShowAddContractForm(false);
        
        // Refresh vendor data
        await fetchVendor();
      } else {
        toast.error(`Failed to add contract: ${response.message}`);
      }
    } catch (error) {
      console.error("Contract creation error:", error);
      const serverErrors = error.response?.data?.errors;
      const message = serverErrors ? serverErrors.join(", ") : (error.response?.data?.message || "Failed to add contract.");
      toast.error(`Contract creation failed: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !vendor) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm">
        <span className="animate-spin h-10 w-10 border-4 rounded-full border-orange-500 border-t-transparent"></span>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden relative transform transition-all duration-300 ease-out"
      >
        {/* Header with Orange Theme */}
        <div className="bg-gradient-to-r from-orange-300 to-orange-400 p-4 sm:p-6 border-b border-orange-500">
          <div className="flex justify-between items-center">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiUser className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-black">
                  Vendor Profile: {vendor.name}
                </h2>
                <div className="flex items-center mt-1 text-black text-sm">
                  <FiFolder className="w-3 h-3 mr-1" />
                  <span>Operations Department</span>
                </div>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white border-b border-gray-200">
          <nav className="flex space-x-1 px-4 overflow-x-auto">
            {[
              { id: "details", label: "Details", icon: <FiUser className="w-4 h-4" /> },
              { id: "documents", label: "Documents", icon: <FiFileText className="w-4 h-4" /> },
              { id: "bank", label: "Bank Details", icon: <FiDollarSign className="w-4 h-4" /> },
              { id: "performance", label: "Performance", icon: <FiStar className="w-4 h-4" /> },
              { id: "contracts", label: "Contracts", icon: <FiAward className="w-4 h-4" /> }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-1 py-3 px-4 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.id
                    ? "text-orange-600 border-b-2 border-orange-500"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {submitting && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <span className="animate-spin h-10 w-10 border-4 rounded-full border-green-500 border-t-transparent"></span>
            </div>
          )}

          {activeTab === "contracts" && contractAddedSuccess && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <FiCheck className="h-5 w-5 text-green-500 mr-2" />
              <p className="text-green-700 font-medium">Contract created successfully!</p>
            </div>
          )}

          {activeTab === "details" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Vendor Information</h3>
                {isEditing ? (
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="animate-spin h-4 w-4 border-2 rounded-full border-white border-t-transparent"></span>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <FiSave className="w-4 h-4" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2"
                  >
                    <FiEdit className="w-4 h-4" />
                    <span>Edit Details</span>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vendor Code
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="font-medium">{vendor.vendorCode}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={editFormData.name}
                      onChange={handleFormChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-medium">{vendor.name}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="email"
                      value={editFormData.email}
                      onChange={handleFormChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-medium">{vendor.email}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="phone"
                      value={editFormData.phone}
                      onChange={handleFormChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-medium">{vendor.phone}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="font-medium">{vendor.category}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    onChange={(e) => isEditing ? handleFormChange(e) : handleStatusUpdate(e.target.value)}
                    name="status"
                    value={isEditing ? editFormData.status || vendor.status : vendor.status}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="blacklisted">Blacklisted</option>
                  </select>
                </div>
              </div>

              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-4">Address Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="street"
                        value={editFormData.address?.street || ""}
                        onChange={handleAddressChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="font-medium">{vendor.address?.street}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="city"
                        value={editFormData.address?.city || ""}
                        onChange={handleAddressChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="font-medium">{vendor.address?.city}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State/Province
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="state"
                        value={editFormData.address?.state || ""}
                        onChange={handleAddressChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="font-medium">{vendor.address?.state}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zip Code
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="zipCode"
                        value={editFormData.address?.zipCode || ""}
                        onChange={handleAddressChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="font-medium">{vendor.address?.zipCode}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Vendor Documents</h3>
              </div>

              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-700">Uploaded Documents</h4>
                {vendor.documents?.length > 0 ? (
                  <ul className="space-y-3">
                    {vendor.documents.map((doc) => (
                      <li
                        key={doc._id}
                        className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center">
                          <FiFileText className="h-5 w-5 text-gray-500 mr-3" />
                          <div>
                            <p className="font-medium">{doc.fileName}</p>
                            <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                              {doc.type}
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-500 hover:text-orange-700 flex items-center text-sm px-3 py-1 rounded-md hover:bg-orange-50"
                          >
                            <FiDownload className="h-4 w-4 mr-1" />
                            View
                          </a>
                          <button
                            onClick={() => handleDocumentRemove(doc._id)}
                            className="text-red-500 hover:text-red-700 flex items-center text-sm px-3 py-1 rounded-md hover:bg-red-50"
                            disabled={submitting}
                          >
                            <FiTrash2 className="h-4 w-4 mr-1" />
                            Remove
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                    <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-gray-500">No documents found.</p>
                  </div>
                )}
              </div>

              <div className="mt-6 p-5 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-md font-medium text-gray-700 mb-3">Upload New Document</h4>
                <div className="flex items-center space-x-3">
                  <label className="flex-1 cursor-pointer">
                    <div 
                      onClick={() => setIsDocUploadModalOpen(true)}
                      className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-all duration-200"
                    >
                      <div className="text-center">
                        <FiFileText className="mx-auto h-8 w-8 text-orange-400" />
                        <p className="mt-1 text-sm text-gray-600">
                          <span className="font-medium text-orange-600">Click to upload documents</span>
                        </p>
                        <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                      </div>
                    </div>
                  </label>
                  {submitting && (
                    <div className="flex justify-center mt-2">
                      <span className="animate-spin h-8 w-8 border-2 rounded-full border-orange-500 border-t-transparent"></span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "bank" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Bank Details</h3>
                {isEditing ? (
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="animate-spin h-4 w-4 border-2 rounded-full border-white border-t-transparent"></span>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <FiSave className="w-4 h-4" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2"
                  >
                    <FiEdit className="w-4 h-4" />
                    <span>Edit Details</span>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="accountName"
                      value={editFormData.bankDetails?.accountName || ""}
                      onChange={handleBankDetailsChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-medium">{vendor.bankDetails?.accountName}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Number
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="accountNumber"
                      value={editFormData.bankDetails?.accountNumber || ""}
                      onChange={handleBankDetailsChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-medium">{vendor.bankDetails?.accountNumber}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="bankName"
                      value={editFormData.bankDetails?.bankName || ""}
                      onChange={handleBankDetailsChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-medium">{vendor.bankDetails?.bankName}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Code
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="bankCode"
                      value={editFormData.bankDetails?.bankCode || ""}
                      onChange={handleBankDetailsChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="font-medium">{vendor.bankDetails?.bankCode}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "performance" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Performance Records</h3>
                <button
                  onClick={() => setShowAddPerformanceForm(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2"
                >
                  <FiPlus className="w-4 h-4" />
                  <span>Add Record</span>
                </button>
              </div>

              {vendor.performance?.length > 0 ? (
                <ul className="space-y-4">
                  {vendor.performance.map((record) => (
                    <li
                      key={record._id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-800">
                            {new Date(record.date).toLocaleDateString()}
                          </p>
                          <div className="flex items-center mt-1">
                            <span className="text-sm text-gray-600 mr-2">Rating:</span>
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <FiStar
                                  key={i}
                                  className={`h-4 w-4 ${
                                    i < record.rating ? "text-yellow-400 fill-current" : "text-gray-300"
                                  }`}
                                />
                              ))}
                              <span className="ml-2 text-sm font-medium text-orange-600">
                                {record.rating}/5
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {record.notes && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Notes:</span> {record.notes}
                          </p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  <FiStar className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-gray-500">No performance records found.</p>
                </div>
              )}

              {showAddPerformanceForm && (
                <div className="mt-6 p-5 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-700 mb-4">Add New Performance Record</h4>
                  <form onSubmit={handleAddPerformance} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FiCalendar className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="date"
                          name="date"
                          value={newPerformance.date}
                          onChange={handlePerformanceChange}
                          required
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Score (1-5)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FiStar className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          name="score"
                          value={newPerformance.score}
                          onChange={handlePerformanceChange}
                          required
                          min="1"
                          max="5"
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Comments
                      </label>
                      <textarea
                        name="comments"
                        value={newPerformance.comments}
                        onChange={handlePerformanceChange}
                        required
                        rows={3}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                    <div className="flex justify-end space-x-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowAddPerformanceForm(false)}
                        disabled={submitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="animate-spin h-4 w-4 border-2 rounded-full border-white border-t-transparent"></span>
                            <span>Adding...</span>
                          </>
                        ) : (
                          <>
                            <FiPlus className="w-4 h-4" />
                            <span>Add Record</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}

          {activeTab === "contracts" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Vendor Contracts</h3>
                <button
                  onClick={() => setShowAddContractForm(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2"
                >
                  <FiPlus className="w-4 h-4" />
                  <span>Add Contract</span>
                </button>
              </div>

              {vendor.contracts?.length > 0 ? (
                <ul className="space-y-4">
                  {vendor.contracts.map((contract) => (
                    <li
                      key={contract._id}
                      className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <h4 className="font-medium text-lg text-orange-600">
                              {contract.title}
                            </h4>
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                contract.status === "active"
                                  ? "bg-green-100 text-green-800"
                                  : contract.status === "draft"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : contract.status === "expired"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {contract.status || "draft"}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm text-gray-600">
                            <p>
                              <span className="font-medium">Contract Number:</span>{" "}
                              {contract.contractNumber || "N/A"}
                            </p>
                            <p>
                              <span className="font-medium">Type:</span>{" "}
                              {contract.type || "N/A"}
                            </p>
                            <p>
                              <span className="font-medium">Value:</span> ${" "}
                              {contract.value ? Number(contract.value).toLocaleString() : "N/A"}
                            </p>
                            <p>
                              <span className="font-medium">Duration:</span>{" "}
                              {new Date(contract.startDate).toLocaleDateString()} -{" "}
                              {new Date(contract.endDate).toLocaleDateString()}
                            </p>
                          </div>
                          {contract.description && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Description:</span> {contract.description}
                              </p>
                            </div>
                          )}
                          {contract.paymentTerms && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Payment Terms:</span> {contract.paymentTerms}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          {contract.contractDocument && (
                            <a
                              href={contract.contractDocument.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50"
                              title="Download contract document"
                            >
                              <FiDownload className="h-5 w-5" />
                            </a>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  <FiAward className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-gray-500">No contracts found.</p>
                </div>
              )}

              {showAddContractForm && (
                <div ref={contractFormRef} className="mt-6 p-5 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-700 mb-4 flex items-center">
                    <FiAward className="h-5 w-5 mr-2 text-orange-500" />
                    Add New Contract
                  </h4>
                  <form onSubmit={handleAddContract} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contract Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={newContract.title}
                          onChange={handleContractChange}
                          required
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Enter contract title"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contract Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="contractNumber"
                          value={newContract.contractNumber}
                          onChange={handleContractChange}
                          required
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Enter unique contract number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contract Value ($)
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiDollarSign className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="number"
                            name="value"
                            value={newContract.value}
                            onChange={handleContractChange}
                            min="0"
                            step="0.01"
                            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contract Type <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiFlag className="h-5 w-5 text-gray-400" />
                          </div>
                          <select
                            name="type"
                            value={newContract.type}
                            onChange={handleContractChange}
                            required
                            className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none"
                          >
                            <option value="">Select type</option>
                            <option value="service">Service</option>
                            <option value="product">Product</option>
                            <option value="licensing">Licensing</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                            <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiCalendar className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="date"
                            name="startDate"
                            value={newContract.startDate}
                            onChange={handleContractChange}
                            required
                            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Date <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiCalendar className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="date"
                            name="endDate"
                            value={newContract.endDate}
                            onChange={handleContractChange}
                            required
                            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={newContract.description}
                        onChange={handleContractChange}
                        rows={3}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="e.g., Scope of work, key terms..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Terms
                      </label>
                      <input
                        type="text"
                        name="paymentTerms"
                        value={newContract.paymentTerms}
                        onChange={handleContractChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="e.g., Net 30 days"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Renewal Terms
                      </label>
                      <input
                        type="text"
                        name="renewalTerms"
                        value={newContract.renewalTerms}
                        onChange={handleContractChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="e.g., Auto-renew, notice period"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Deliverables
                      </label>
                      <input
                        type="text"
                        name="deliverables"
                        value={newContract.deliverables}
                        onChange={handleContractChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="e.g., Monthly reports, quarterly reviews"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contract Document
                      </label>
                      <div 
                        onClick={() => setIsContractDocModalOpen(true)}
                        className={`flex items-center justify-center px-6 py-4 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ${
                          newContract.contractDocument 
                            ? "border-green-300 bg-green-50" 
                            : "border-gray-300 hover:border-orange-400 hover:bg-orange-50"
                        }`}
                      >
                        {newContract.contractDocument ? (
                          <div className="flex items-center space-x-2 text-green-700">
                            <FiCheck className="w-5 h-5" />
                            <span className="font-medium">{newContract.contractDocument.originalName || newContract.contractDocument.fileName}</span>
                            <span className="text-xs text-green-600">(Click to change)</span>
                          </div>
                        ) : (
                          <div className="text-center">
                            <FiFileText className="mx-auto h-6 w-6 text-gray-400 mb-1" />
                            <p className="text-sm text-gray-600">
                              <span className="font-medium text-orange-600">Click to select/upload contract</span>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowAddContractForm(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-200"
                        disabled={submitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="animate-spin h-4 w-4 border-2 rounded-full border-white border-t-transparent"></span>
                            <span>Adding...</span>
                          </>
                        ) : (
                          <>
                            <FiPlus className="w-4 h-4" />
                            <span>Add Contract</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Upload Modals */}
      <UploadDocumentModal
        isOpen={isDocUploadModalOpen}
        onClose={() => setIsDocUploadModalOpen(false)}
        entityId={vendorId}
        entityType="Vendor"
        category="Vendor Documents"
        onUploadSuccessWithFiles={handleDocumentUploadSuccess}
      />

      <UploadDocumentModal
        isOpen={isContractDocModalOpen}
        onClose={() => setIsContractDocModalOpen(false)}
        entityId={vendorId}
        entityType="Vendor"
        category="Agreements & Contracts"
        onUploadSuccessWithFiles={handleContractDocSuccess}
      />
    </div>
  </div>
);
};

export default VendorProfileModal;